import { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// material-ui
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import MuiBreadcrumbs, { BreadcrumbsProps } from '@mui/material/Breadcrumbs';

// project imports
import MainCard from '@/components/MainCard';
import navigation from '@/menu-items';
import { getFirstAccessiblePath } from '@/menu-items/first-accessible-path';
import { useAuthContext } from '@/providers';
import { ThemeDirection } from '@/theme/config';

// assets
import HomeOutlined from '@ant-design/icons/HomeOutlined';

// types
import { NavItemType } from '@/types/menu';
import { useBreadcrumbExtras } from './breadcrumb-extras';

// Romanian labels for route segments that aren't backed by a menu entry
// (detail / create / edit pages, routing groups, etc.).
const SEGMENT_LABELS: Record<string, string> = {
	common: 'Acasă',
	dashboard: 'Dashboard',
	project: 'Proiecte',
	chat: 'Chat',
	users: 'Utilizatori',
	'users-admin': 'Administratori',
	'users-moderator': 'Moderatori',
	'users-fsp': 'Administrații',
	administrations: 'Administrații',
	requests: 'Cereri',
	moderator: 'Moderatori',
	fsp: 'Administrații',
	clause: 'Clauze',
	tc: 'Termeni și condiții',
	pp: 'Confidențialitate',
	gdpr: 'Cookies',
	feedback: 'Feedback',
	bug: 'Probleme raportate',
	compliment: 'Complimente',
	suggestion: 'Sugestii',
	version: 'Versiuni',
	nomenclatoare: 'Nomenclatoare',
	'status-proiect': 'Stagiu proiect',
	judete: 'Județe',
	uat: 'UAT-uri',
	'tipuri-anunt': 'Tipuri de anunț',
	'stiluri-geometrie': 'Stiluri de geometrie',
	'tile-uri': 'Tile-uri hartă',
	editeaza: 'Editare',
	nou: 'Adăugare',
	types: 'Tipuri proiecte',
	profile: 'Profil',
	create: 'Adăugare',
	edit: 'Editare'
};

// action segments whose following id is a route param, not a "Detalii" crumb
const ACTION_SEGMENTS = new Set(['edit', 'create']);

// segments that are routing-only and shouldn't appear as a crumb (prefix / wrapper paths).
// `announcment` și `consultation` sunt aici fiindcă NU mai au pagină-listă: conținutul aparține
// unui proiect sau unui cartier, iar un crumb pe segmentul de prefix ar fi un link mort.
const HIDDEN_SEGMENTS = new Set(['common', 'view', 'announcment', 'consultation']);

// build { '/clean/path': 'Title' } from the menu items (ignoring query-string urls,
// which are ambiguous filter views)
function buildMenuLabels(
	items: NavItemType[] = [],
	acc: Record<string, string> = {}
): Record<string, string> {
	items.forEach((node) => {
		if (node.url && typeof node.title === 'string' && !node.url.includes('?')) {
			acc[node.url] = node.title;
		}
		if (node.children) buildMenuLabels(node.children, acc);
	});
	return acc;
}

const menuLabels = buildMenuLabels(navigation?.items ?? []);

const isIdSegment = (seg: string) => /^\d+$/.test(seg) || /^[0-9a-f-]{16,}$/i.test(seg);

const humanize = (seg: string) => {
	const text = seg.replace(/-/g, ' ');
	return text.charAt(0).toUpperCase() + text.slice(1);
};

interface Crumb {
	path: string;
	label: string;
}

interface Props {
	title?: boolean;
	divider?: boolean;
	maxItems?: number;
	sx?: BreadcrumbsProps['sx'];
}

// ==============================|| BREADCRUMBS ||============================== //

export default function Breadcrumbs({
	title = true,
	divider = false,
	maxItems = 8,
	sx
}: Props) {
	const theme = useTheme();
	const location = useLocation();
	// Etichetele derivate din meniu pot fi chei i18n (ex. menu.versioning) — t() le
	// traduce, iar textul brut trece neschimbat.
	const { t } = useTranslation();
	// „Acasă" din breadcrumb duce la prima pagină accesibilă rolului curent.
	const { user } = useAuthContext();

	const iconSX = {
		marginRight: theme.direction === ThemeDirection.RTL ? 0 : theme.spacing(0.5),
		marginLeft: theme.direction === ThemeDirection.RTL ? theme.spacing(0.5) : 0,
		width: '1rem',
		height: '1rem',
		color: theme.vars.palette.secondary.main
	};

	// vederile din query-string (ex. ?view=<id>) publică crumb-uri suplimentare aici
	const extras = useBreadcrumbExtras();

	const segments = location.pathname.split('/').filter(Boolean);

	// build the cumulative trail, skipping the "common" routing prefix from the display
	const crumbs: Crumb[] = [];
	let acc = '';
	segments.forEach((seg, index) => {
		acc += `/${seg}`;
		if (HIDDEN_SEGMENTS.has(seg)) return;
		// an id that belongs to an action route (e.g. /edit/:id) is just a param, not a
		// "Detalii" view — the action segment ("Editare") is the meaningful trailing crumb.
		if (isIdSegment(seg) && ACTION_SEGMENTS.has(segments[index - 1])) return;
		const label = t(
			menuLabels[acc] ??
				SEGMENT_LABELS[seg] ??
				(isIdSegment(seg) ? 'Detalii' : humanize(seg))
		);
		crumbs.push({ path: acc, label });
	});

	extras.forEach((extra) => crumbs.push({ path: extra.path, label: extra.label }));

	if (crumbs.length === 0) return <Typography />;

	const currentLabel = crumbs[crumbs.length - 1].label;

	const breadcrumbContent: ReactElement = (
		<MainCard
			border={false}
			sx={{ mb: 1, bgcolor: 'inherit', backgroundImage: 'none', ...sx }}
			content={false}
			shadow="none"
		>
			<MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems} separator="/">
				<Typography
					component={Link}
					to={getFirstAccessiblePath(user)}
					sx={{
						display: 'flex',
						alignItems: 'center',
						color: 'text.secondary',
						textDecoration: 'none'
					}}
				>
					<HomeOutlined style={{ ...iconSX, marginRight: 0 }} />
				</Typography>
				{crumbs.map((crumb, index) => {
					const isLast = index === crumbs.length - 1;
					return isLast ? (
						<Typography
							key={crumb.path}
							variant="subtitle1"
							sx={{ color: 'text.primary' }}
						>
							{crumb.label}
						</Typography>
					) : (
						<Typography
							key={crumb.path}
							component={Link}
							to={crumb.path}
							variant="h6"
							sx={{ color: 'text.secondary', textDecoration: 'none' }}
						>
							{crumb.label}
						</Typography>
					);
				})}
			</MuiBreadcrumbs>
			{title && (
				<Typography variant="h2" sx={{ mt: 1.25 }}>
					{currentLabel}
				</Typography>
			)}
			{divider && <Divider sx={{ mt: 1 }} />}
		</MainCard>
	);

	return breadcrumbContent;
}
